import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import {
  FileText, FileType, Image as ImageIcon, Plus, X, Loader2, Copy, Trash2, Download,
  Send, Eye, Edit3, BookOpen, Upload, Search,
  CheckCircle2, XCircle, Clock, RefreshCw, Percent,
  Building2, Hammer, FileSpreadsheet, ChevronRight, ChevronDown,
  AlertTriangle, History, ArrowRight, ListChecks,
  Ban, CalendarDays, Paperclip, FilePlus, Archive,
  RotateCcw, Users, TrendingUp, Target,
  Sparkles, BarChart3, PieChart as PieChartIcon, Wallet
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Legend } from "recharts";
import axios from "axios";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { WORKFLOW_STEPS, STATUS_META, NEXT_STEP_LABEL, TIPO_META, UNIDADES, DEFAULT_PCT, opts, fmt } from "./constants";
import { Modal, DeadlineBadge, WorkflowStepper, daysUntil, getWfIndex } from "./SharedComponents";
import ErrorBoundary from "../../components/ErrorBoundary";

export function AttachmentsModal({ open, onClose, budget, onUpdated }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchFiles = useCallback(async () => {
    if (!budget?._id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/budgets/${budget._id}/attachments`, opts);
      setFiles(res.data || []);
    } catch { toast.error("Error al cargar archivos"); }
    finally { setLoading(false); }
  }, [budget?._id]);

  useEffect(() => { if (open) fetchFiles(); }, [open, fetchFiles]);

  // FIX (Fase 1 QA regresión) — el modal no se cerraba con Escape (solo con el
  // botón X o el backdrop).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const handleUpload = async () => {
    const file = fileRef.current?.files[0];
    if (!file) { toast.error("Selecciona un archivo"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await axios.post(`${API}/budgets/${budget._id}/attachments`, fd, { ...opts, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} subido correctamente`);
      if (fileRef.current) fileRef.current.value = "";
      fetchFiles();
      onUpdated?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir archivo"); }
    finally { setUploading(false); }
  };

  const handleDelete = async (attId) => {
    if (!window.confirm("¿Eliminar este archivo?")) return;
    try {
      await axios.delete(`${API}/budgets/${budget._id}/attachments/${attId}`, opts);
      toast.success("Archivo eliminado");
      fetchFiles();
      onUpdated?.();
    } catch { toast.error("Error al eliminar"); }
  };

  const fmtSize = (b) => {
    if (!b) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  const iconForExt = (ext) => {
    if (!ext) return FileText;
    const e = ext.toLowerCase();
    if ([".png", ".jpg", ".jpeg", ".webp"].includes(e)) return ImageIcon;
    if ([".xlsx", ".xls"].includes(e)) return FileSpreadsheet;
    if ([".dwg", ".doc", ".docx"].includes(e)) return FileType;
    return FileText;
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2"><Paperclip size={18} /> Archivos Adjuntos</h3>
            <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md">{budget?.nombre}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4 flex-1 overflow-y-auto">
          {/* Upload zone */}
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <p className="text-xs font-bold text-purple-800 mb-2 uppercase">Subir nuevo archivo</p>
            <p className="text-xs text-purple-600 mb-3">Excel, DWG, PDF, imágenes (máx 10 MB)</p>
            <div className="flex gap-2">
              <input type="file" ref={fileRef} accept=".pdf,.xlsx,.xls,.dwg,.png,.jpg,.jpeg,.webp,.doc,.docx"
                className="flex-1 border border-purple-300 rounded-xl px-3 py-2 text-sm bg-white file:mr-3 file:text-xs file:font-semibold file:bg-purple-600 file:text-white file:border-0 file:rounded-lg file:px-3 file:py-1" data-testid="attachment-file-input" />
              <button onClick={handleUpload} disabled={uploading}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="attachment-upload-btn">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                Subir
              </button>
            </div>
          </div>

          {/* Files list */}
          <ErrorBoundary
            resetKey={budget?._id}
            fallback={
              <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-gray-500" data-testid="attachments-error-fallback">
                <Paperclip size={28} className="text-amber-500" />
                <p className="text-sm font-medium">Error al cargar adjuntos</p>
                <p className="text-xs text-gray-400">Cerrá y volvé a abrir esta ventana.</p>
              </div>
            }
          >
          {loading ? (
            <div className="space-y-2">{[1, 2].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
          ) : files.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Paperclip size={32} className="mx-auto mb-2" />
              <p className="text-sm">Aún no hay archivos adjuntos</p>
            </div>
          ) : (
            <div className="space-y-2">
              {files.map(att => {
                const Icon = iconForExt(att.ext);
                return (
                  <div key={att.id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-purple-300 transition-colors" data-testid={`attachment-row-${att.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Icon size={18} className="text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{att.filename}</p>
                      <p className="text-xs text-gray-400">{fmtSize(att.size)} · {att.uploaded_by} · {att.uploaded_at?.slice(0, 10)}</p>
                    </div>
                    <a href={`${API}/budgets/${budget._id}/attachments/${att.id}`} target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold flex items-center gap-1 transition-colors" data-testid={`attachment-download-${att.id}`}>
                      <Download size={12} /> Bajar
                    </a>
                    <button onClick={() => handleDelete(att.id)} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`attachment-delete-${att.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          </ErrorBoundary>
        </div>
      </div>
    </div>
  , document.body);
}

// ─── Papelera Modal ──────────────────────────────────────────────────────────
